import { useState } from "react";
import { axiosPrivate } from "../api/axios";
import BenefitDetails from "./benefitDetails";
import { toast } from "react-toastify";
import apiRoutes from "../api/apiRoutes";
import TotalContribution from "./totalContribution";
import { IoCheckmark } from "react-icons/io5";

function ContributionTypeCard({
  contributionType,
  savingsType,
  paymentPlan,
  tpdAmount,
  sumAssured,
  basicContribution,
  singleContribution,
  flexibleId,
  deathBenefitValue,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState({});
  const [paymentFrequency, setPaymentFrequency] = useState(
    paymentPlan || "monthly",
  );

  const [benefitData, setBenefitData] = useState([]);
  const [totalContributionData, setTotalContributionData] = useState([]);
  const [openBenefit, setOpenBenefit] = useState({
    close: true,
    open: false,
  });

  const [isApplicable, setIsApplicable] = useState(true);

  const applicableOptions = [
    {
      id: 0,
      value: true,
      title: "Yes",
    },
    {
      id: 1,
      value: false,
      title: "No",
    },
  ];

  const [inputs, setInputs] = useState({
    basicContribution: basicContribution
      ? Number(basicContribution).toLocaleString()
      : "",
    singleContribution: singleContribution
      ? Number(singleContribution).toLocaleString()
      : "",
    initSum: sumAssured ? Number(sumAssured).toLocaleString() : "",
    // tpdAmount: tpdAmount,
  });

  const handleInputs = (name, value) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenBenefit = (id, value) => {
    setOpenBenefit((prev) => ({
      ...prev,
      [id]: value,
      open: !prev.open,
      close: !prev.close,
    }));
  };

  const handleUpdateBenefit = async (updatedFrequency) => {
    const nextPaymentFrequency =
      typeof updatedFrequency === "string"
        ? updatedFrequency
        : paymentFrequency;

    try {
      setIsLoading(true);
      setErr({});

      const payload = {
        savings_type: savingsType,
        basic_contribution_amount: Number(
          inputs.basicContribution.toString().replace(/,/g, ""),
          inputs.basicContribution.toString().replace(/,/g, ""),
        ),
        single_contribution_amount: Number(
          inputs.singleContribution.toString().replace(/,/g, ""),
          inputs.singleContribution.toString().replace(/,/g, ""),
        ),
        payment_insurance_plan: nextPaymentFrequency,
        initial_sum_assured: Number(
          inputs.initSum.toString().replace(/,/g, ""),
          inputs.initSum.toString().replace(/,/g, ""),
        ),
        is_sum_assured_applicable: isApplicable,
      };

      const response = await axiosPrivate.patch(
        `${apiRoutes.updateBenefits}/${flexibleId}`,
        payload,
      );

      if (response?.data) {
        setIsLoading(false);
        setOpenBenefit({ open: true, close: false });
        setBenefitData(response.data.data);
        setTotalContributionData(response.data.data);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0]?.message ||
          "An error occurred while updating benefits",
      );

      const errorMap = {};
      error?.response?.data?.errors?.forEach((e) => {
        if (e.field && e.message) {
          errorMap[e.field] = e.message;
        }
      });

      setErr(errorMap);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setErr({});
      }, 5000);
    }
  };

  const frequencyOptions = ["monthly", "quarterly", "half-yearly", "yearly"];

  const contributionCalculation = (paymentPlan, amount) => {
    if (!amount) return 0;

    const multipliers = {
      monthly: 1,
      quarterly: 3,
      "half-yearly": 6,
      yearly: 12,
    };

    const discounts = {
      monthly: 0,
      quarterly: 0.75, // 0.75% discount
      halfYearly: 1.5, // 1.5% discount
      yearly: 3, // 3% discount
    };

    const multiplier = multipliers[paymentPlan] || 1;
    // const discountFactor = (100 - (discounts[paymentPlan] || 0)) / 100;

    return amount * multiplier;
  };

  return (
    <main className="tw-font-haas">
      <section className="tw-flex tw-flex-col md:tw-flex-row tw-items-start tw-gap-3">
        <div className="capitalize tw-space-y-2 tw-bg-[#F9F9F9] tw-border tw-border-[#E5E5E5] tw-rounded-xl tw-w-[500px] tw-h-auto tw-p-8">
          <h2 className="capitalize tw-mb-2 tw-text-lg tw-text-black/50">
            {contributionType.charAt(0).toUpperCase() +
              contributionType.slice(1)}
          </h2>

          {/* Frequency */}
          {(contributionType === "regular" ||
            contributionType === "single-regular") && (
            <label htmlFor="contributionfrequency" className="tw-w-full">
              <span className="tw-text-sm tw-text-black">Frequency</span>
              <select
                name="contributionfrequency"
                value={paymentFrequency}
                onChange={(e) => {
                  setPaymentFrequency(e.target.value);
                  if (openBenefit.open) {
                    handleUpdateBenefit(e.target.value);
                  }
                }}
                className="form-select tw-capitalize tw-w-full"
              >
                {frequencyOptions.map((type, i) => (
                  <option key={i} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {err.payment_insurance_plan && (
                <span className="tw-text-sm tw-text-[red]">
                  {err.payment_insurance_plan}
                </span>
              )}
            </label>
          )}

          {/* Regular Contribution */}
          {(contributionType === "regular" ||
            contributionType === "single-regular") && (
            <label htmlFor="basicContribution" className="tw-w-full">
              <span className="tw-text-sm tw-text-black">
                Monthly Contribution
              </span>
              <input
                id="basicContribution"
                className="input-control"
                type="text"
                value={inputs.basicContribution}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, "");
                  if (!isNaN(raw)) {
                    handleInputs("basicContribution", raw);
                  }
                }}
                onBlur={() => {
                  const val = Number(
                    inputs.basicContribution.toString().replace(/,/g, ""),
                  );
                  handleInputs(
                    "basicContribution",
                    isNaN(val) ? "" : val.toLocaleString(),
                  );
                }}
                onFocus={() => {
                  const raw = inputs.basicContribution.replace(/,/g, "");
                  handleInputs("basicContribution", raw);
                }}
              />
              {err.basic_contribution_amount && (
                <span className="tw-text-sm tw-text-[red]">
                  {err.basic_contribution_amount}
                </span>
              )}
            </label>
          )}

          {/* Single Contribution */}
          {contributionType !== "regular" && (
            <label htmlFor="singleContribution" className="tw-w-full">
              <span className="tw-text-sm tw-text-black">
                Single Contribution
              </span>
              <input
                id="singleContribution"
                className="input-control"
                type="text"
                value={inputs.singleContribution}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, "");
                  if (!isNaN(raw)) {
                    handleInputs("singleContribution", raw);
                  }
                }}
                onBlur={() => {
                  const val = Number(
                    inputs.singleContribution.toString().replace(/,/g, ""),
                  );
                  handleInputs(
                    "singleContribution",
                    isNaN(val) ? "" : val.toLocaleString(),
                  );
                }}
                onFocus={() => {
                  const raw = inputs.singleContribution.replace(/,/g, "");
                  handleInputs("singleContribution", raw);
                }}
              />
              {err.single_contribution_amount && (
                <span className="tw-text-sm tw-text-[red]">
                  {err.single_contribution_amount}
                </span>
              )}
            </label>
          )}

          <div className="tw-w-full tw-grid tw-grid-cols-4 tw-gap-3">
            <div className="tw-col-span-2">
              <label htmlFor="deathamount">
                <span className="tw-text-sm tw-text-black">Sum Assured</span>
                <input
                  id="initSum"
                  className="input-control"
                  type="text"
                  disabled={contributionType === "single"}
                  value={
                    contributionType === "single"
                      ? Number(100000).toLocaleString()
                      : inputs.initSum
                  }
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    if (!isNaN(raw)) {
                      handleInputs("initSum", raw);
                    }
                  }}
                  onBlur={() => {
                    const val = Number(
                      inputs.initSum.toString().replace(/,/g, ""),
                    );
                    handleInputs(
                      "initSum",
                      isNaN(val) ? "" : val.toLocaleString(),
                    );
                  }}
                  onFocus={() => {
                    const raw = inputs.initSum.replace(/,/g, "");
                    handleInputs("initSum", raw);
                  }}
                />
                {err.initial_sum_assured && (
                  <span className="tw-text-sm tw-text-[red]">
                    {err.initial_sum_assured}
                  </span>
                )}
              </label>
            </div>
            <div className="tw-col-span-2">
              <span className="tw-text-sm tw-text-black">
                Is Sum Assured Applicable
              </span>
              <div className="tw-w-full tw-flex tw-items-center tw-gap-2">
                {applicableOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => setIsApplicable(option.value)}
                    className="tw-w-full tw-h-12 tw-bg-white tw-border tw-border-black/20 tw-rounded-lg tw-flex tw-items-center tw-gap-2 tw-px-5 tw-cursor-pointer"
                  >
                    <IoCheckmark
                      className={` ${
                        isApplicable === option.value
                          ? "!tw-bg-[#BEC663]"
                          : "tw-border tw-border-black/20"
                      } tw-bg-transparent tw-rounded-full tw-w-5 tw-h-5 tw-p-1 tw-text-white `}
                    />
                    {option.title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={() => handleUpdateBenefit()}
            type="button"
            className="tw-mt-4 tw-text-white tw-font-medium tw-text-sm tw-w-full tw-h-12 tw-rounded-lg tw-bg-[#BEC663]"
          >
            {isLoading ? "Calculating..." : "Calculate Benefit Details"}
          </button>
        </div>

        {openBenefit.open && (
          <>
            {totalContributionData?.flexibleResult?.savingsType ===
              "single-regular" && (
              <div className="tw-flex tw-flex-col tw-gap-2 tw-items-start">
                <TotalContribution
                  className="tw-bg-white"
                  totalContributionTitle={`Basic Regular Premium`}
                  contributionValue={contributionCalculation(
                    totalContributionData?.flexibleResult?.paymentInsurancePlan,
                    totalContributionData?.flexibleResult
                      ?.basicContributionAmount,
                  )}
                  deathBenefitValue={contributionCalculation(
                    totalContributionData?.flexibleResult?.paymentInsurancePlan,
                    totalContributionData?.flexibleResult?.death,
                  )}
                  tpdDetails={contributionCalculation(
                    totalContributionData?.flexibleResult?.paymentInsurancePlan,
                    totalContributionData?.flexibleResult?.tpd,
                  )}
                  totalMonthlyPayable={`Total Premium Payable - ${totalContributionData?.flexibleResult?.paymentInsurancePlan}`}
                  totalMonthlyPayableValue={
                    totalContributionData?.flexibleResult
                      ?.paymentInsurancePlan === "monthly"
                      ? totalContributionData?.flexibleResult
                          ?.monthlyPremiumDefault
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "quarterly"
                        ? totalContributionData?.flexibleResult
                            ?.quarterlyPremiumDefault
                        : totalContributionData?.flexibleResult
                              ?.paymentInsurancePlan === "half-yearly"
                          ? totalContributionData?.flexibleResult
                              ?.halfYearlyPremiumDefault
                          : totalContributionData?.flexibleResult
                                ?.yearlyPremiumDefault?.paymentInsurancePlan ===
                              "quarterly"
                            ? totalContributionData?.flexibleResult
                                ?.quarterlyPremiumDefault
                            : totalContributionData?.flexibleResult
                                  ?.paymentInsurancePlan === "half-yearly"
                              ? totalContributionData?.flexibleResult
                                  ?.halfYearlyPremiumDefault
                              : totalContributionData?.flexibleResult
                                  ?.yearlyPremiumDefault
                  }
                  notSingle={
                    totalContributionData?.flexibleResult?.savingsType !==
                    "single"
                  }
                  discount={
                    totalContributionData?.flexibleResult
                      ?.paymentInsurancePlan === "yearly"
                      ? totalContributionData?.flexibleRates?.yearlyRate
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "quarterly"
                        ? Number(
                            totalContributionData?.flexibleRates?.quarterlyRate,
                          )
                        : totalContributionData?.flexibleResult
                              ?.paymentInsurancePlan === "half-yearly"
                          ? totalContributionData?.flexibleRates?.halfYearlyRate
                          : (0)?.paymentInsurancePlan === "quarterly"
                            ? Number(
                                totalContributionData?.flexibleRates
                                  ?.quarterlyRate,
                              )
                            : totalContributionData?.flexibleResult
                                  ?.paymentInsurancePlan === "half-yearly"
                              ? totalContributionData?.flexibleRates
                                  ?.halfYearlyRate
                              : 0
                  }
                  fscPercentage={
                    totalContributionData?.flexibleFscAndAdminFeesResult
                      ?.percentage?.fscFee
                  }
                  fscFeeValue={
                    totalContributionData?.flexibleFscAndAdminFeesResult?.[
                      totalContributionData?.flexibleResult
                        ?.paymentInsurancePlan === "half-yearly"
                        ? "halfYearly"
                        : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan
                    ]?.fscFee
                  }
                  adminPercentage={
                    totalContributionData?.flexibleFscAndAdminFeesResult
                      ?.percentage?.adminPolicyFee
                  }
                  adminFeeValue={
                    totalContributionData?.flexibleFscAndAdminFeesResult?.[
                      totalContributionData?.flexibleResult
                        ?.paymentInsurancePlan === "half-yearly"
                        ? "halfYearly"
                        : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan
                    ]?.adminPolicyFee
                  }
                />
                <TotalContribution
                  totalContributionTitle={`Basic Single Premium`}
                  contributionValue={
                    totalContributionData?.flexibleResult
                      ?.singleContributionAmount
                  }
                  deathBenefitValue={0}
                  tpdDetails={0}
                  totalMonthlyPayable={`Total Premium Payable - ${
                    totalContributionData?.flexibleResult?.savingsType ===
                    "single-regular"
                      ? "Single"
                      : totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan
                  }`}
                  totalMonthlyPayableValue={
                    totalContributionData?.flexibleResult?.savingsType ===
                    "single-regular"
                      ? totalContributionData?.flexibleResult
                          ?.singleContributionAmount
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "monthly"
                        ? totalContributionData?.flexibleResult
                            ?.monthlyPremiumDefault
                        : totalContributionData?.flexibleResult
                              ?.paymentInsurancePlan === "quarterly"
                          ? totalContributionData?.flexibleResult
                              ?.quarterlyPremiumDefault
                          : totalContributionData?.flexibleResult
                                ?.paymentInsurancePlan === "half-yearly"
                            ? totalContributionData?.flexibleResult
                                ?.halfYearlyPremiumDefault
                            : totalContributionData?.flexibleResult
                                  ?.yearlyPremiumDefault
                                  ?.paymentInsurancePlan === "monthly"
                              ? totalContributionData?.flexibleResult
                                  ?.monthlyPremiumDefault
                              : totalContributionData?.flexibleResult
                                    ?.paymentInsurancePlan === "quarterly"
                                ? totalContributionData?.flexibleResult
                                    ?.quarterlyPremiumDefault
                                : totalContributionData?.flexibleResult
                                      ?.paymentInsurancePlan === "half-yearly"
                                  ? totalContributionData?.flexibleResult
                                      ?.halfYearlyPremiumDefault
                                  : totalContributionData?.flexibleResult
                                      ?.yearlyPremiumDefault
                  }
                  notSingle={
                    totalContributionData?.flexibleResult?.savingsType !==
                    "single-regular"
                  }
                  discount={
                    totalContributionData?.flexibleResult
                      ?.paymentInsurancePlan === "yearly"
                      ? totalContributionData?.flexibleRates?.yearlyRate
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "quarterly"
                        ? Number(
                            totalContributionData?.flexibleRates?.quarterlyRate,
                          )
                        : totalContributionData?.flexibleResult
                              ?.paymentInsurancePlan === "half-yearly"
                          ? totalContributionData?.flexibleRates?.halfYearlyRate
                          : 0
                  }
                  fscPercentage={
                    totalContributionData?.flexibleFscAndAdminFeesResult
                      ?.percentage?.fscFee
                  }
                  fscFeeValue={
                    totalContributionData?.flexibleFscAndAdminFeesResult?.[
                      totalContributionData?.flexibleResult
                        ?.paymentInsurancePlan === "half-yearly"
                        ? "halfYearly"
                        : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan
                    ]?.fscFee
                  }
                  adminPercentage={
                    totalContributionData?.flexibleFscAndAdminFeesResult
                      ?.percentage?.adminPolicyFee
                  }
                  adminFeeValue={
                    totalContributionData?.flexibleFscAndAdminFeesResult?.[
                      totalContributionData?.flexibleResult
                        ?.paymentInsurancePlan === "half-yearly"
                        ? "halfYearly"
                        : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan
                    ]?.adminPolicyFee
                  }
                />
              </div>
            )}

            {/* Show single card for other types */}
            {totalContributionData?.flexibleResult?.savingsType !==
              "single-regular" && (
              <TotalContribution
                totalContributionTitle={`Basic ${totalContributionData?.flexibleResult?.savingsType} Premium`}
                contributionValue={
                  totalContributionData?.flexibleResult?.savingsType ===
                  "single"
                    ? totalContributionData?.flexibleResult
                        ?.singleContributionAmount
                    : contributionCalculation(
                        totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan,
                        totalContributionData?.flexibleResult
                          ?.basicContributionAmount,
                      )
                }
                deathBenefitValue={
                  totalContributionData?.flexibleResult?.savingsType ===
                  "single"
                    ? 0
                    : totalContributionData?.flexibleResult?.death
                }
                tpdDetails={
                  totalContributionData?.flexibleResult?.savingsType ===
                  "single"
                    ? 0
                    : totalContributionData?.flexibleResult?.tpd
                }
                totalMonthlyPayable={`Total Premium Payable - ${
                  totalContributionData?.flexibleResult?.savingsType ===
                  "single"
                    ? "Single"
                    : totalContributionData?.flexibleResult
                        ?.paymentInsurancePlan
                }`}
                totalMonthlyPayableValue={
                  totalContributionData?.flexibleResult?.savingsType ===
                  "single"
                    ? totalContributionData?.flexibleResult
                        ?.singleContributionAmount
                    : totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan === "monthly"
                      ? totalContributionData?.flexibleResult
                          ?.monthlyPremiumDefault
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "quarterly"
                        ? totalContributionData?.flexibleResult
                            ?.quarterlyPremiumDefault
                        : totalContributionData?.flexibleResult
                              ?.paymentInsurancePlan === "half-yearly"
                          ? totalContributionData?.flexibleResult
                              ?.halfYearlyPremiumDefault
                          : totalContributionData?.flexibleResult
                                ?.yearlyPremiumDefault?.paymentInsurancePlan ===
                              "monthly"
                            ? totalContributionData?.flexibleResult
                                ?.monthlyPremiumDefault
                            : totalContributionData?.flexibleResult
                                  ?.paymentInsurancePlan === "quarterly"
                              ? totalContributionData?.flexibleResult
                                  ?.quarterlyPremiumDefault
                              : totalContributionData?.flexibleResult
                                    ?.paymentInsurancePlan === "half-yearly"
                                ? totalContributionData?.flexibleResult
                                    ?.halfYearlyPremiumDefault
                                : totalContributionData?.flexibleResult
                                    ?.yearlyPremiumDefault
                }
                notSingle={
                  totalContributionData?.flexibleResult?.savingsType !==
                  "single"
                }
                discount={
                  totalContributionData?.flexibleResult
                    ?.paymentInsurancePlan === "yearly"
                    ? totalContributionData?.flexibleRates?.yearlyRate
                    : totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan === "quarterly"
                      ? Number(
                          totalContributionData?.flexibleRates?.quarterlyRate,
                        )
                      : totalContributionData?.flexibleResult
                            ?.paymentInsurancePlan === "half-yearly"
                        ? totalContributionData?.flexibleRates?.halfYearlyRate
                        : (0)?.paymentInsurancePlan === "quarterly"
                          ? Number(
                              totalContributionData?.flexibleRates
                                ?.quarterlyRate,
                            )
                          : totalContributionData?.flexibleResult
                                ?.paymentInsurancePlan === "half-yearly"
                            ? totalContributionData?.flexibleRates
                                ?.halfYearlyRate
                            : 0
                }
                fscPercentage={
                  totalContributionData?.flexibleFscAndAdminFeesResult
                    ?.percentage?.fscFee
                }
                fscFeeValue={
                  totalContributionData?.flexibleFscAndAdminFeesResult?.[
                    totalContributionData?.flexibleResult
                      ?.paymentInsurancePlan === "half-yearly"
                      ? "halfYearly"
                      : totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan
                  ]?.fscFee
                }
                adminPercentage={
                  totalContributionData?.flexibleFscAndAdminFeesResult
                    ?.percentage?.adminPolicyFee
                }
                adminFeeValue={
                  totalContributionData?.flexibleFscAndAdminFeesResult?.[
                    totalContributionData?.flexibleResult
                      ?.paymentInsurancePlan === "half-yearly"
                      ? "halfYearly"
                      : totalContributionData?.flexibleResult
                          ?.paymentInsurancePlan
                  ]?.adminPolicyFee
                }
              />
            )}
          </>
        )}
      </section>

      {openBenefit.open && (
        <section>
          {isLoading ? (
            <div
              style={{ marginTop: 150 }}
              className="d-flex flex-column justify-content-center align-items-center"
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Calculating. Please wait...</div>
            </div>
          ) : (
            <BenefitDetails
              close={() => setOpenBenefit({ open: false, close: true })}
              conservativePpaMaturity={
                benefitData.conservativeData.totalPpaAsMaturityDate
                  ? benefitData.conservativeData.totalPpaAsMaturityDate.toLocaleString()
                  : 0
              }
              cyear1={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "10years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "10years"
                    ].toLocaleString()
                  : 0
              }
              cyear2={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "15years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "15years"
                    ].toLocaleString()
                  : 0
              }
              cyear3={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "20years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "20years"
                    ].toLocaleString()
                  : 0
              }
              cyear4={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "25years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "25years"
                    ].toLocaleString()
                  : 0
              }
              cyear5={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "30years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "30years"
                    ].toLocaleString()
                  : 0
              }
              cyear6={
                benefitData.conservativeData.estimateCashWithdrawalAmount[
                  "35years"
                ]
                  ? benefitData.conservativeData.estimateCashWithdrawalAmount[
                      "35years"
                    ].toLocaleString()
                  : 0
              }
              conversativePpaCashWithdrawal={
                benefitData.conservativeData
                  .totalPpaAsMaturityDateWithCashWithdrawal
              }
              moderatePpaMaturity={
                benefitData.moderateData.totalPpaAsMaturityDate
                  ? benefitData.moderateData.totalPpaAsMaturityDate.toLocaleString()
                  : 0
              }
              myear1={
                benefitData.moderateData.estimateCashWithdrawalAmount["10years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "10years"
                    ].toLocaleString()
                  : 0
              }
              myear2={
                benefitData.moderateData.estimateCashWithdrawalAmount["15years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "15years"
                    ].toLocaleString()
                  : 0
              }
              myear3={
                benefitData.moderateData.estimateCashWithdrawalAmount["20years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "20years"
                    ].toLocaleString()
                  : 0
              }
              myear4={
                benefitData.moderateData.estimateCashWithdrawalAmount["25years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "25years"
                    ].toLocaleString()
                  : 0
              }
              myear5={
                benefitData.moderateData.estimateCashWithdrawalAmount["30years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "30years"
                    ].toLocaleString()
                  : 0
              }
              myear6={
                benefitData.moderateData.estimateCashWithdrawalAmount["35years"]
                  ? benefitData.moderateData.estimateCashWithdrawalAmount[
                      "35years"
                    ].toLocaleString()
                  : 0
              }
              assertivePpaMaturity={
                benefitData.assertiveData.totalPpaAsMaturityDate
                  ? benefitData.assertiveData.totalPpaAsMaturityDate.toLocaleString()
                  : 0
              }
              ayear1={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "10years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "10years"
                    ].toLocaleString()
                  : 0
              }
              ayear2={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "15years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "15years"
                    ].toLocaleString()
                  : 0
              }
              ayear3={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "20years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "20years"
                    ].toLocaleString()
                  : 0
              }
              ayear4={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "25years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "25years"
                    ].toLocaleString()
                  : 0
              }
              ayear5={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "30years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "30years"
                    ].toLocaleString()
                  : 0
              }
              ayear6={
                benefitData.assertiveData.estimateCashWithdrawalAmount[
                  "35years"
                ]
                  ? benefitData.assertiveData.estimateCashWithdrawalAmount[
                      "35years"
                    ].toLocaleString()
                  : 0
              }
            />
          )}
        </section>
      )}
    </main>
  );
}

export default ContributionTypeCard;
